import {
  type BaseSyntheticEvent,
  type Dispatch,
  type SetStateAction,
  useEffect,
  useState,
} from "react";
import {
  type FieldErrors,
  type UseFormRegister,
  useForm,
} from "react-hook-form";
import type { IAddress } from "../types/address.types";
import { countryCodeShaper, phoneNumberShaper } from "../utils";
import {
  parseKuwaitAddressFields,
  parseMakaniValue,
} from "../components/Address/AddressForm/address-form.helper";
import { EAddressFields, EAddressType } from "../constants";

export interface IUseAddressFormReceiverDetails {
  changeReceiverDetails: boolean;
  isPhoneVerified: boolean;
  phone: string;
}

export interface IUseAddressFormErrorStates {
  addressTypeError: string;
  areaError: boolean;
  cityError: boolean;
  floorError: string;
  invalidPhoneNumber: boolean;
  numberError: string;
  zoneError: boolean;
}

export interface IUseAddressFormQatarStates {
  qatarSelectedCountryCode: string;
  qatarCountryDisplayLabel: JSX.Element | null;
  selectedArea: string;
  selectedCity: string;
  selectedZone: string;
}

export interface IUseAddressFormOtherStates {
  addressType: string[];
  defaultAddress: boolean;
  finalAddress: IAddress;
  showConfirmAddressSheet: boolean;
}

export interface IUseAddressFormStates {
  receiverDetails: IUseAddressFormReceiverDetails;
  errorStates: IUseAddressFormErrorStates;
  qatarStates: IUseAddressFormQatarStates;
  otherStates: IUseAddressFormOtherStates;
}

export interface IUseAddressFormSetters {
  setReceiverDetails: Dispatch<SetStateAction<IUseAddressFormReceiverDetails>>;
  setErrorStates: Dispatch<SetStateAction<IUseAddressFormErrorStates>>;
  setQatarStates: Dispatch<SetStateAction<IUseAddressFormQatarStates>>;
  setOtherStates: Dispatch<SetStateAction<IUseAddressFormOtherStates>>;
}

export interface IUseAddressFormReturn {
  addressFormSetters: IUseAddressFormSetters;
  addressFormStates: IUseAddressFormStates;
  errors: FieldErrors<IAddress>;
  getValues: () => IAddress;
  handleSubmit: (
    onValid: (data: IAddress) => void,
    onInvalid?: (errors: FieldErrors<IAddress>) => void,
  ) => (e?: BaseSyntheticEvent) => Promise<void>;
  register: UseFormRegister<IAddress>;
  reset: () => void;
}

export const useAddressForm = (address: IAddress): IUseAddressFormReturn => {
  const {
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<IAddress>();

  watch();
  const formData = getValues();

  useEffect(() => {
    reset({
      ...formData,
      ...address,
      countryCode: address.phone && countryCodeShaper(address.phone || ""),
      phone:
        address.phone && phoneNumberShaper(formData.phone || address.phone),
      firstName: formData.firstName || address.firstName,
    });

    if (otherStates.addressType.length === 0) {
      setOtherStates((prev) => ({
        ...prev,
        addressType: [address.addressLabel || EAddressType.HOME],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unnecessary dependency
  }, [address]);

  const [receiverDetails, setReceiverDetails] =
    useState<IUseAddressFormReceiverDetails>({
      changeReceiverDetails: false,
      isPhoneVerified: address.isPhoneVerified || false,
      phone: "",
    });

  const [errorStates, setErrorStates] = useState<IUseAddressFormErrorStates>({
    addressTypeError: "",
    areaError: false,
    cityError: false,
    floorError: "",
    invalidPhoneNumber: false,
    numberError: "",
    zoneError: false,
  });

  const [qatarStates, setQatarStates] = useState<IUseAddressFormQatarStates>({
    qatarSelectedCountryCode: "",
    qatarCountryDisplayLabel: null,
    selectedArea: "",
    selectedCity: "",
    selectedZone: "",
  });

  const [otherStates, setOtherStates] = useState<IUseAddressFormOtherStates>({
    addressType: [],
    defaultAddress: false,
    finalAddress: {},
    showConfirmAddressSheet: false,
  });

  useEffect(() => {
    const makaniValue = address.makani ?? "";

    const parsedMakani = parseMakaniValue(makaniValue);

    if (makaniValue) {
      const { zone, area, city } = parsedMakani;

      addressFormSetters.setQatarStates((prev) => ({
        ...prev,
        selectedCity: city ?? "",
        selectedZone: zone ?? "",
        selectedArea: area ?? "",
      }));
    }

    const { number, block, building, floor } = parseKuwaitAddressFields(
      address.appartment ?? "",
      address.building ?? "",
    );

    if (floor || number || block) {
      setValue(EAddressFields.NUMBER, number);
      setValue(EAddressFields.BLOCK, block);
      setValue(EAddressFields.BUILDING, building);
      setValue(EAddressFields.FLOOR, floor);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- unnecessary dependency
  }, [address]);

  const addressFormStates = {
    receiverDetails,
    errorStates,
    qatarStates,
    otherStates,
  };

  const addressFormSetters = {
    setReceiverDetails,
    setErrorStates,
    setQatarStates,
    setOtherStates,
  };

  return {
    addressFormSetters,
    addressFormStates,
    errors,
    getValues,
    handleSubmit,
    register,
    reset,
  };
};
